import { FileSystem } from '@/cordova/FileSystem';
import { apis } from './api';
import { VoiceRecorder } from 'capacitor-voice-recorder';
// Recomendación: Verifica que en android/app/src/main/AndroidManifest.xml tengas:
// <uses-permission android:name="android.permission.RECORD_AUDIO" />
// <uses-permission android:name="android.permission.WRITE_EXTERNAL_STORAGE" />
// <uses-permission android:name="android.permission.READ_EXTERNAL_STORAGE" />
import { LocalNotifications } from '@capacitor/local-notifications';
import {
    getSoundExtension,
    resolveAndroidNotificationChannel,
    resolveInAppSound,
    resolveNotificationSound
} from '@/config/notificationSound';

const ANDROID_PACKAGE_FALLBACK = 'com.movilizame.holataxi';

function getPlatform () {
    return ((window.device && window.device.platform) || '').toLowerCase();
}

function getAndroidPackageName () {
    const appDirectory = window.cordova && window.cordova.file && window.cordova.file.applicationDirectory;
    const packageMatch = appDirectory && appDirectory.match(/file:\/\/\/data\/user\/0\/([^/]+)\//);
    return (packageMatch && packageMatch[1]) || ANDROID_PACKAGE_FALLBACK;
}

async function playSystemNotificationSound (fileName, url) {
    const ext = getSoundExtension(fileName);
    const notification = {
        id: Date.now() % 2147483647,
        title: '',
        body: '',
        sound: `${fileName}.${ext}`,
        schedule: { at: new Date(Date.now() + 300) }
    };

    if (getPlatform() === 'android') {
        notification.channelId = resolveAndroidNotificationChannel(url);
    }

    await LocalNotifications.schedule({
        notifications: [
            notification
        ]
    });
}

function buildMediaUrl (fileName) {
    const ext = getSoundExtension(fileName);
    if (getPlatform() === 'android') {
        return 'android.resource://' + getAndroidPackageName() + '/raw/' + fileName;
    }
    if (getPlatform() === 'ios') {
        // cordova-plugin-media en iOS lee desde www/static/audio (no .caf del bundle)
        return 'static/audio/' + fileName + '.' + ext;
    }
    if (window.cordova && window.cordova.file && window.cordova.file.applicationDirectory) {
        return window.cordova.file.applicationDirectory + 'www/static/audio/' + fileName + '.' + ext;
    }
    return 'static/audio/' + fileName + '.' + ext;
}

function buildStaticAudioUrl (fileName) {
    return 'static/audio/' + fileName + '.' + getSoundExtension(fileName);
}

function playHtmlAudioFallback (fileName, onError) {
    try {
        const audio = new Audio(buildStaticAudioUrl(fileName));
        const result = audio.play();
        if (result && result.catch) {
            result.catch(onError);
        }
        return audio;
    } catch (error) {
        onError(error);
        return null;
    }
}

function playCustomSound (fileName, url, errorLabel) {
    if (fileName && window.Media) {
        const soundUrl = buildMediaUrl(fileName);
        console.log('Playing custom sound from:', soundUrl);
        var media = new window.Media(soundUrl,
            function () {
                media.release();
            },
            function (error) {
                media.release();
                console.error(errorLabel, soundUrl, error);
                playHtmlAudioFallback(fileName, function (htmlError) {
                    console.error('Error playing HTML audio fallback:', buildStaticAudioUrl(fileName), htmlError);
                    playSystemNotificationSound(fileName, url);
                });
            }
        );
        media.play();
        return media;
    }
    return playHtmlAudioFallback(fileName, function (htmlError) {
        console.error('Error playing HTML audio:', buildStaticAudioUrl(fileName), htmlError);
        playSystemNotificationSound(fileName, url);
    });
}

let recordInProgress = false;
/**
 * [TODO] Ver de usar require
 */
export function playSound (file, url) {
    const soundBase = resolveInAppSound(file, url);
    console.log('play sound', window.Media, soundBase, url);
    return playCustomSound(soundBase, url, 'Error playing sound:');
}

export function playNotificationSound (soundFile, url) {
    const fileName = resolveNotificationSound(soundFile, url);
    console.log('playNotificationSound', fileName, url, 'legacy:', soundFile);
    return playCustomSound(fileName, url, 'Error playing notification sound:');
}

export function requestPermission () {
    // Solicita el permiso y retorna el resultado
    return VoiceRecorder.requestAudioRecordingPermission().then(r => r.value);
}

export class MediaAudio {
    constructor ({ id, checksum, viajeId } = {}) {
        this.fs = new FileSystem();
        this.id = id;
        this.checksum = checksum;
        this.viajeId = viajeId;
        this.exists = false;
        this.corrupted = false;
        this.media = false;
        this.downloadInProgress = false;
        this.position = 0;
        this.duration = 0;
        this.positionPercentage = 0;
        this.durationMinutes = 0;
        this.durationSeconds = 0;
        this.playing = false;

        this._playingInterval = null;

        this.onProgress = (event) => {
            this.position = this.media.currentTime;
            this.positionPercentage = Math.round((this.position / this.duration) * 100);
            console.log(this.positionPercentage);
        };

        this.onEndPlay = (event) => {
            this.endPlay();
        };

        if (!id) {
            this.persistent = false;
            this.name = (new Date()).getTime();
            this.filename = this.fs.makeFilename(this.name, 'aac', this.persistent);
            console.log(this.filename);
        } else {
            this.persistent = true;
            this.filename = this.fs.makeFilename(this.id, 'aac', this.persistent);
            this.checkInDisk();
            console.log(this.filename);
        }
    }

    async checkInDisk () {
        // await this.fs.delete(this.filename);
        this.fs.check(this.filename, this.checksum).then(() => {
            this.exists = true;
            this.createMedia();
        }).catch((error) => {
            console.log('---------------------------');
            console.log(error.message);
            if (error.message === 'corrupted') {
                this.corrupted = true;
                this.exists = false;
                // this.download();
            } else if (error.message) {
                this.exists = false;
                this.corrupted = true;
                this.download();
            } else {
                this.exists = false;
            }
        });
    }

    setId (id) {
        this.id = id;
        this.persistent = true;
        this.exists = true;
        this.filename = this.fs.makeFilename(this.id, 'aac', this.persistent);
        this.createMedia();
    }

    async createMedia () {
        this.promiseMedia = new Promise(async (resolve, reject) => {
            const ext = this.fs.filenameExtension(this.filename);
            const data = await this.fs.readFile(this.filename);
            this.media = new Audio(`data:audio/${ext};base64,${data}`);
            this.media.addEventListener('loadeddata', () => {
                this.ready = true;
                this.playing = false;
                this.duration = this.media.duration;

                this.durationMinutes = Math.floor(this.duration / 60);
                this.durationSeconds = Math.ceil(this.duration - this.durationMinutes * 60);
                if (this.durationSeconds < 10) {
                    this.durationSeconds = '0' + this.durationSeconds;
                }
                // console.log(this.duration);
                // console.log(this.durationMinutes);
                // console.log(this.durationSeconds);
                return resolve();
            });
        });
        return this.promiseMedia;
    }

    download () {
        this.corrupted = false;
        this.exists = false;
        this.downloadInProgress = true;
        // [TODO] Make URL
        let url = apis.mensajesApi.downloadURL(this.id);
        let dst = this.filename;
        return this.fs.download({ dst, url }).then(() => {
            this.downloadInProgress = false;
            return this.checkInDisk();
        }).catch((err) => {
            console.log('me fui por aca');
            console.log(err);
            this.exists = false;
            this.downloadInProgress = false;
            return Promise.reject();
        });
    }

    upload () {
        let path = this.filename;
        console.log('find file before upload', path);
        this.fs.find(path).then(() => {
            console.log('file founded');
        }).catch((ex) => {
            console.log('file founded, ex', ex);
        });
        let url = apis.mensajesApi.uploadURL();
        let fileKey = 'audio';
        let fileName;
        // [TODO] Mejorar el handler de la plataforma
        if (window.device.platform === 'android') {
            fileName = 'temp.aac';
        } else {
            fileName = 'temp.aac';
        }
        let params = {
            viaje_id: this.viajeId
        };
        console.log('fs.upload', url, path, fileKey, fileName, params);
        return this.fs.upload({url, path, fileKey, fileName, params}).then((mensaje) => {
            // let mensaje = JSON.parse(r.response);
            return this.fs.copy(this.filename, `DATA://${mensaje.id}.aac`).then(() => {
                this.setId(mensaje.id);
                return Promise.resolve(mensaje);
            }).catch((e) => {
                console.log('copy error', e);
                return Promise.reject();
            });
        }).catch((e) => {
            console.log('upload error', e);
            return Promise.reject();
        });
    }

    /**
     * PLAY
     */

    play () {
        // if (this._playingInterval) {
        //     clearInterval(this._playingInterval);
        //     this._playingInterval = null;
        // }
        this.playing = true;
        this.media.play();

        this.media.addEventListener('timeupdate', this.onProgress);
        this.media.addEventListener('ended', this.onEndPlay);

        // this._playingInterval = setInterval(() => {
        //     // get media position
        //     this.position += 50 / 1000;
        //     this.positionPercentage = (this.position / this.duration) * 100;
        // }, 50);
    }

    /**
     * PAUSE
     */

    pause () {
        if (this.media) {
            this.media.pause();
            // clearInterval(this._playingInterval);
            // this._playingInterval = null;
            this.playing = false;
        }
    }

    endPlay () {
        this.positionPercentage = 100;
        this.playing = false;
        this.position = 0;
        this.media.removeEventListener('timeupdate', this.onProgress);
        this.media.removeEventListener('ended', this.onEndPlay);
    }

    /**
     * startRecord
     */

    async startRecord () {
        if (recordInProgress) {
            console.log('Error, hay otro audio en progreso.');
            return null;
        }
        // Solicitar permiso antes de grabar
        const hasPermission = await VoiceRecorder.requestAudioRecordingPermission();
        if (!hasPermission.value) {
            console.error('Permiso de grabación de audio denegado.');
            throw new Error('Permiso de grabación de audio denegado.');
        }
        recordInProgress = true;
        try {
            const result = await VoiceRecorder.startRecording();
            if (result.value) {
                return true;
            } else {
                throw new Error('Error al iniciar la grabación de audio');
            }
        } catch (e) {
            recordInProgress = false;
            console.error('Error al iniciar la grabación:', e);
            throw e;
        }
    }

    cancelRecord () {
        console.log('Cancelando grabación...');
        recordInProgress = false;
        try {
            return VoiceRecorder.stopRecording();
        } catch (e) {
            console.error('Error al cancelar la grabación:', e);
        }
    }

    /**
     * StopRecord
     */
    async stopRecord () {
        recordInProgress = false;
        try {
            const result = await VoiceRecorder.stopRecording();
            if (!result.value) {
                throw new Error('No se obtuvo audio grabado');
            }
            const { recordDataBase64, mimeType } = result.value;
            const ext = mimeType.split('/')[1];
            this.filename = this.fs.makeFilename(this.name, ext, false);
            await this.fs.writeFile(this.filename, recordDataBase64);
        } catch (e) {
            console.error('Error al detener la grabación:', e);
            throw e;
        }
    }

}



// WEBPACK FOOTER //
// ./src/services/Audio.js